import { cx } from '@/styled-system/css';
import { forwardRef } from 'react';
import { getGradeLabel, getSchoolTypeLabel, type NearbySchool } from './schoolUtils';
import {
  link,
  ratingBadge,
  ratingBadgeMuted,
  schoolBody,
  schoolDistance,
  schoolItem,
  schoolItemFocusTarget,
  schoolMeta,
  schoolName,
  srOnly,
} from './nearbySchoolsModule.styles';

type SchoolListItemProps = {
  school: NearbySchool;
};

export const SchoolListItem = forwardRef<HTMLLIElement, SchoolListItemProps>(({ school }, ref) => {
  const typeLabel = getSchoolTypeLabel(school);
  const gradeLabel = getGradeLabel(school.lowGrade, school.highGrade);
  const isUniversity = school.type === 'university';
  const hasRating = typeof school.rating === 'number' && school.rating > 0;
  const ratingLabel = hasRating ? `GreatSchools rating: ${school.rating} out of 10.` : 'Not rated by GreatSchools.';
  const distanceLabel = `${school.distance} miles from this listing`;

  return (
    <li className={`${schoolItem} ${schoolItemFocusTarget}`} ref={ref} tabIndex={-1}>
      {isUniversity ? (
        <span aria-hidden="true" />
      ) : (
        <span role="img" aria-label={ratingLabel} className={cx(ratingBadge, !hasRating && ratingBadgeMuted)}>
          {hasRating ? school.rating : '-'}
        </span>
      )}
      <div className={schoolBody}>
        <h3 className={schoolName}>
          <a href={school.uriV2} className={link}>
            {school.name}
          </a>
        </h3>
        <p className={schoolMeta}>
          <span>{typeLabel}</span>
          {gradeLabel && (
            <>
              <span aria-hidden="true">{' · '}</span>
              <span className={srOnly}>, </span>
              <span>{gradeLabel}</span>
            </>
          )}
        </p>
      </div>
      <span className={schoolDistance} aria-label={distanceLabel}>
        {school.distance} mi
      </span>
    </li>
  );
});

SchoolListItem.displayName = 'SchoolListItem';
